import { useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import useSubscription from "./useSubscription";
import {
  GOD_ONLY_DASHBOARDS,
  PLAN_DASHBOARDS,
} from "../config/planAccess";

/**
 * Decide quais telas o usuário pode acessar com base no cargo e no plano.
 *
 * Regras:
 * - Telas god só para cargo `god`.
 * - God vê tudo.
 * - Sem assinatura ativa (trial/trial_expired/pending) → todas as telas não-god liberadas.
 * - Com assinatura ativa → apenas as telas do plano.
 */
export default function usePlanAccess() {
  const { user } = useAuth();
  const { plan, isActive, isGod: subIsGod, isLegacy } = useSubscription();
  const isGod = user?.cargo === "god" || subIsGod;

  const canAccessDashboard = useCallback(
    (dashboardId) => {
      if (GOD_ONLY_DASHBOARDS.includes(dashboardId)) return isGod;
      if (isGod) return true;
      // Conta legado (sem billingClass / sem assinatura no Mercado Pago) tem
      // acesso vitalício e não deve ser restringida ao plano "starter" só
      // porque não existe um documento de subscription — sem essa checagem,
      // qualquer membro de um projeto legado (mesmo admin do projeto) ficava
      // limitado às 4 telas do starter.
      if (isLegacy) return true;
      if (!isActive) return true; // trial/pending: acesso total (exceto god)
      const allowed = PLAN_DASHBOARDS[plan?.id || plan?.slug || 'starter'] || PLAN_DASHBOARDS.starter;
      return allowed.includes(dashboardId);
    },
    [isGod, isLegacy, isActive, plan]
  );

  return { canAccessDashboard, plan, isGod };
}
